import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Ban, CheckCircle2, Coins, Repeat } from "lucide-react";
import { fmtEur } from "@/lib/vg2Format";
import { generateDecision, getDecision, newChoiceOpId, postOwnerChoice } from "@/lib/vg2Decidi";
import { setRequestAssumptions } from "@/lib/vg3StructuredRequest";
import { DecidiInsight } from "@/components/vg2/DecidiInsight";

const SEM = {
  GREEN: ["bg-emerald-100 text-emerald-800", "Potenziale forte"],
  AMBER: ["bg-amber-100 text-amber-800", "Potenziale moderato"],
  RED: ["bg-red-100 text-red-800", "Potenziale debole"],
  DA_CHIARIRE: ["bg-slate-200 text-slate-700", "Da chiarire"],
};

const range = (r) => (r && r.low != null ? (r.low === r.high ? fmtEur(r.low) : `${fmtEur(r.low)} – ${fmtEur(r.high)}`) : null);
const pair = (p) => (p ? range({ low: p[0], high: p[1] }) : null);

function Row({ label, value, tid, strong }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-1 text-sm" data-testid={tid}>
      <span className="text-vg2-muted">{label}</span>
      <span className={`whitespace-nowrap ${strong ? "font-semibold text-vg2-ink" : "text-vg2-ink"}`}>{value}</span>
    </div>
  );
}

function ShareControl({ value, disabled, onChange, tid, chosen }) {
  const opts = [0.3, 0.4, 0.5, 0.6, 0.7];
  return (
    <label className="md:col-span-2 flex flex-wrap items-center gap-2 text-xs text-vg2-ink" data-testid={`${tid}-share`}>
      Superficie dell'unità in vendita:
      <select value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))}
              className="border border-vg2-line rounded-md px-2 py-1 bg-white" data-testid={`${tid}-share-select`}>
        {opts.map((o) => <option key={o} value={o}>{Math.round(o * 100)}%</option>)}
      </select>
      <span className="text-vg2-muted" data-testid={`${tid}-share-hint`}>
        {chosen ? "modificando l'ipotesi la scelta già fatta andrà confermata di nuovo" : "modifica l'ipotesi e ricalcola"}
      </span>
    </label>
  );
}

function MixedBreakdown({ m, tid, onShare, busy, chosen }) {
  return (
    <div className="grid md:grid-cols-2 gap-3 mt-3" data-testid={`${tid}-mixed`}>
      {onShare && <ShareControl value={m.share_sell} disabled={busy} onChange={onShare} tid={tid} chosen={chosen} />}
      <div className="rounded-lg border border-vg2-line p-3" data-testid={`${tid}-mixed-capital`}>
        <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-vg2-muted mb-1">
          <Coins size={14} /> Capitale una tantum · unità in vendita ({Math.round(m.sqm_sell)} m²)
        </p>
        <Row label="Ricavo lordo" value={pair(m.capital.gross_sale)} tid={`${tid}-mixed-gross`} />
        <Row label="Frazionamento + opere" value={pair(m.capital.split_and_works)} tid={`${tid}-mixed-split`} />
        <Row label="Costi di vendita" value={pair(m.capital.sale_costs)} tid={`${tid}-mixed-salecosts`} />
        <Row label="Capitale netto" value={pair(m.capital.net_capital)} tid={`${tid}-mixed-net`} strong />
      </div>
      <div className="rounded-lg border border-vg2-line p-3" data-testid={`${tid}-mixed-income`}>
        <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-vg2-muted mb-1">
          <Repeat size={14} /> Reddito ricorrente · unità in locazione ({Math.round(m.sqm_rent)} m²)
        </p>
        {m.income ? (
          <>
            <Row label="Canone lordo annuo" value={pair(m.income.annual_gross)} tid={`${tid}-mixed-rent-gross`} />
            <Row label="Reddito netto annuo" value={pair(m.income.annual_net)} tid={`${tid}-mixed-rent-net`} strong />
          </>
        ) : <p className="text-sm text-vg2-muted">Canone non stimabile con i dati disponibili.</p>}
        <Row label="Valore dell'unità che resta tua" value={fmtEur(m.kept_unit_value)} tid={`${tid}-mixed-kept`} />
      </div>
      <p className="md:col-span-2 text-xs text-vg2-ink" data-testid={`${tid}-mixed-note`}>{m.note}</p>
      <ul className="md:col-span-2 text-xs text-vg2-muted list-disc pl-4 space-y-0.5" data-testid={`${tid}-mixed-assumptions`}>
        {m.assumptions.map((a) => <li key={a}>{a}</li>)}
      </ul>
    </div>
  );
}

export function Economics({ e, tid, onShare, busy, chosen, hideInvest, flush }) {
  if (!e) return null;
  if (e.mixed_split) return <MixedBreakdown m={e.mixed_split} tid={tid} onShare={onShare} busy={busy} chosen={chosen} />;
  const hasAny = e.net_proceeds_range || e.annual_net_rent_range;
  return (
    <div className={flush ? "" : "mt-3"} data-testid={`${tid}-econ`}>
      {e.net_proceeds_range && (
        <>
          <Row label="Ricavo lordo" value={range(e.gross_proceeds_range)} tid={`${tid}-gross`} />
          <Row label="Costi" value={range(e.upfront_costs_range)} tid={`${tid}-costs`} />
          <Row label="Ricavo netto (capitale)" value={range(e.net_proceeds_range)} tid={`${tid}-net`} strong />
        </>
      )}
      {e.annual_net_rent_range && (
        <>
          {e.upfront_costs_range && !e.net_proceeds_range && !hideInvest && (
            <Row label={e.strategy_category === "short_term_rent" && !e.preparation_costs_range ? "Costi di avvio" : "Investimento iniziale"}
                 value={range(e.upfront_costs_range)} tid={`${tid}-invest`} />
          )}
          <Row label="Canone lordo annuo" value={range(e.annual_gross_rent_range)} tid={`${tid}-rent-gross`} />
          <Row label={e.short_term_model?.exploratory ? "Reddito netto annuo · stima esplorativa" : "Reddito netto annuo"}
               value={range(e.annual_net_rent_range)} tid={`${tid}-rent-net`} strong />
          {e.short_term_model && e.annual_net_rent_central != null && (
            <Row label="Valore centrale più probabile" value={fmtEur(e.annual_net_rent_central)} tid={`${tid}-rent-central`} />
          )}
        </>
      )}
      {e.time_to_execute_months && (
        <Row label="Tempi" value={`${e.time_to_execute_months.low}–${e.time_to_execute_months.high} mesi`} tid={`${tid}-time`} />
      )}
      {!hasAny && (
        <p className="text-sm text-vg2-muted" data-testid={`${tid}-no-econ`}>
          {(e.caveats || [])[0] || "Stima economica non producibile con i dati disponibili."}
        </p>
      )}
    </div>
  );
}

function OperationCard({ card, tid, chosen, busy, onChoose, onShare }) {
  const [cls, semLabel] = SEM[card.semaphore?.state] || SEM.DA_CHIARIRE;
  const vincolo = card.feasibility === "vincolo";
  return (
    <article className={`rounded-xl border p-4 md:p-5 bg-white ${chosen ? "border-vg2-red" : "border-vg2-line"}`} data-testid={tid}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-display font-semibold text-lg leading-snug" data-testid={`${tid}-title`}>{card.title}</h3>
          {card.request_label && (
            <p className="text-xs text-vg2-muted mt-0.5" data-testid={`${tid}-request-label`}>Hai chiesto: «{card.request_label}»</p>
          )}
          {/^Rimodernare/.test(card.request_label || "") && /^RENOVATE/.test(card.scenario_key)
            && /pesante/i.test(card.economic_analysis?.intervention_level_label || card.economic_analysis?.upfront_costs_range?.label || "") && (
            <p className="text-xs text-amber-800 mt-0.5" data-testid={`${tid}-remod-note`}>
              Per ora il rimodernamento è calcolato come ristrutturazione completa: costi e tempi possono risultare più alti.
            </p>
          )}
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full ${cls}`} data-testid={`${tid}-semaphore`}>
          {card.analysis_metrics?.kind === "none" ? "Non calcolabile" : <>{semLabel}{card.score != null ? ` · ${card.score}/100` : ""}</>}
        </span>
      </div>
      {vincolo && (
        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-300 p-3 text-sm" data-testid={`${tid}-constraint`}>
          <p className="flex items-center gap-1.5 font-medium"><AlertTriangle size={15} /> Vincolo da superare: non è realizzabile subito</p>
          <ul className="list-disc pl-5 mt-1 space-y-0.5 text-xs">
            {card.feasibility_notes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        </div>
      )}
      <Economics e={card.economic_analysis} tid={tid} onShare={onShare} busy={busy} chosen={chosen} />
      {keyLine(card.analysis_metrics, card.scenario_key) && (
        <p className="mt-2 text-sm font-medium text-vg2-ink" data-testid={`${tid}-key`}>{keyLine(card.analysis_metrics, card.scenario_key)}</p>
      )}
      {!vincolo && (card.request_origin === "suggested" ? (card.feasibility_notes || []).slice(0, 1)
        : (card.feasibility_notes || []).filter((n) => /^(Acquisto:|L'immobile è da|Lavori localizzati|Prima di affittare servono|Lettura della richiesta semplificata)/.test(n))).map((n, i) => (
        <p key={n} className="mt-2 text-xs text-amber-800 flex items-start gap-1.5" data-testid={`${tid}-prerequisite${i ? `-${i}` : ""}`}>
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {n}
        </p>
      ))}
      {!vincolo && card.analysis_metrics?.works_component?.method && (
        <p className="mt-1 text-xs text-vg2-muted" data-testid={`${tid}-works-method`}>
          Stima lavori: {card.analysis_metrics.works_component.method}
          {card.analysis_metrics.works_component.costs ? ` = ${pair(card.analysis_metrics.works_component.costs)}` : ""}
          {card.analysis_metrics.works_component.exploratory ? " · esplorativa" : ""}
        </p>
      )}
      <div className="mt-4 flex items-center gap-3">
        <button type="button" disabled={busy} onClick={() => onChoose(card)} data-testid={`${tid}-analyze`}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm bg-vg2-red text-white transition-opacity disabled:opacity-60">
          {chosen ? <CheckCircle2 size={15} /> : <ArrowRight size={15} />}
          {chosen ? "Scelta · rivedi l'analisi" : "Analizza questa possibilità"}
        </button>
      </div>
    </article>
  );
}

const fmtR = (a) => (a ? range({ low: a[0], high: a[2] }) : "—");

export function chooseOperation(view, caseId, scenarioKey) {
  const type = scenarioKey === view.primary_scenario_key ? "CONFIRM_PRIMARY" : "CHOOSE_ALTERNATIVE";
  return postOwnerChoice(caseId, {
    decision_result_id: view.decision_result_id, choice_type: type,
    selected_scenario_key: scenarioKey, operation_id: newChoiceOpId(caseId, type, scenarioKey),
  });
}

export function resultLine(m) {
  if (!m) return "—";
  if (m.kind === "sale") return <><span className="whitespace-nowrap">{fmtR(m.net)}</span> netti (una tantum)</>;
  if (m.kind === "income") return <><span className="whitespace-nowrap">{fmtR(m.annual_net)}</span> netti l'anno</>;
  if (m.kind === "none") return `non calcolabile: ${m.missing_reason}`;
  return "capitale + reddito (vedi scheda)";
}

// Sintesi confrontabile: utile incrementale per le vendite, rendimento e recupero per i redditi.
export function keyLine(m, category) {
  if (!m) return null;
  if (m.kind === "sale" && category === "SELL_NOW") return "Riferimento del confronto";
  if (m.kind === "sale" && m.vs_sell_now) {
    const d = m.vs_sell_now.incremental_net[1];
    return `${d >= 0 ? "+" : "−"}${fmtEur(Math.abs(d))} rispetto a vendere subito (stima centrale)`;
  }
  if (m.kind === "income" && m.yield_on_total_pct != null) {
    return `Rende circa il ${String(m.yield_on_total_pct).replace(".", ",")}% l'anno sul capitale impegnato · recupero in circa ${Math.round(m.payback_total_years)} anni`;
  }
  return null;
}

export function RequestedOperationsPanel({ view, caseId, onChosen, onViewChange }) {
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const ref = view?.structured_request_ref;
  if (!ref) return null;
  const cards = view.scenarios || [];
  const requested = cards.filter((c) => c.request_origin === "requested");
  const suggested = cards.filter((c) => c.request_origin === "suggested");
  const chosenKey = view.owner_decision?.selected_scenario_key;

  const changeShare = async (share) => {
    setBusy(true); setErr(null);
    try {
      await setRequestAssumptions(ref.journey_id, share);
      await generateDecision(caseId, `d-share-${caseId}`);
      onViewChange?.(await getDecision(caseId));
    } catch { setErr("Ricalcolo non riuscito: riprova."); }
    setBusy(false);
  };

  const choose = (card) => navigate(`/vg2/analizza/${caseId}/${card.scenario_key}`, { state: { view } });

  return (
    <section className="mb-12" data-testid="requested-operations">
      <DecidiInsight caseId={caseId} decisionResultId={view.decision_result_id} />
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-1">
        {requested.length || (ref.not_calculable || []).length ? "Le operazioni che hai chiesto di valutare" : "Hai chiesto solo una valutazione: nessuna operazione specifica"}
      </p>
      <p className="text-xs text-vg2-muted mb-4" data-testid="requested-operations-version">
        Calcolate sulla versione {ref.version} della tua richiesta · numeri indicativi, da verificare con un professionista.
      </p>
      {ref.stale && (
        <p className="text-sm text-amber-800 mb-4" data-testid="requested-operations-stale">
          La richiesta è passata alla versione {ref.current_version}: queste operazioni si riferiscono alla versione {ref.version}.
        </p>
      )}
      <div className="space-y-4" data-testid="requested-operations-list">
        {requested.map((c, i) => (
          <OperationCard key={c.scenario_key} card={c} tid={`requested-op-${i}`} busy={busy}
                         chosen={c.scenario_key === chosenKey} onChoose={choose}
                         onShare={onViewChange && ref.journey_id ? changeShare : null} />
        ))}
      </div>
      {(ref.not_calculable || []).map((n, i) => (
        <p key={n.op_id} className="mt-3 text-sm text-vg2-muted" data-testid={`requested-op-missing-${i}`}>
          «{n.label}»: {n.reason}.
        </p>
      ))}
      {(ref.excluded || []).length > 0 && (
        <p className="mt-4 flex items-center gap-1.5 text-sm text-vg2-muted" data-testid="requested-operations-excluded">
          <Ban size={14} /> Escluse su tua indicazione: {ref.excluded.join(", ")}.
        </p>
      )}
      {suggested.length > 0 && (
        <div className="mt-10" data-testid="suggested-operations">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-1">Alternative suggerite da ValutaGenio</p>
          <p className="text-xs text-vg2-muted mb-4">Non le hai chieste tu: le mostriamo solo come confronto (massimo {ref.alternatives_max}).</p>
          <div className="space-y-4">
            {suggested.map((c, i) => (
              <OperationCard key={c.scenario_key} card={c} tid={`suggested-op-${i}`} busy={busy}
                             chosen={c.scenario_key === chosenKey} onChoose={choose} />
            ))}
          </div>
        </div>
      )}
      {err && <p className="text-sm text-vg2-error mt-3" data-testid="requested-operations-error">{err}</p>}
    </section>
  );
}
